import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RegisterPage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/register`,
        { name, email, password },
        { withCredentials: true }
      );
      // Store token in localStorage
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      toast.success('Akaun berjaya didaftarkan!');
      navigate('/dashboard', { state: { user: response.data } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Pendaftaran gagal');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/google/login`);
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error('Gagal memulakan log masuk Google');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" style={{
      background: 'linear-gradient(135deg, #3D348B 0%, #7678ED 100%)'
    }}>
      <Helmet>
        <title>Daftar Akaun | Kawkad</title>
      </Helmet>
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 right-10 w-72 h-72 bg-[#F7B801]/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#F18701]/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Kawkad</h1>
          <p className="text-white/70">Cipta akaun baharu</p>
        </div>

        <div className="bg-white/95 backdrop-blur-lg rounded-3xl shadow-2xl p-8">
          <form onSubmit={handleRegister} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#3D348B] font-medium">Nama Penuh</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ahmad bin Abdullah"
                required
                data-testid="register-name-input"
                className="bg-white border-gray-200 focus:border-[#7678ED] focus:ring-[#7678ED]/20 rounded-xl h-12"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#3D348B] font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nama@email.com"
                required
                data-testid="register-email-input"
                className="bg-white border-gray-200 focus:border-[#7678ED] focus:ring-[#7678ED]/20 rounded-xl h-12"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#3D348B] font-medium">Kata Laluan</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="register-password-input"
                className="bg-white border-gray-200 focus:border-[#7678ED] focus:ring-[#7678ED]/20 rounded-xl h-12"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="register-submit-btn"
              className="w-full bg-[#3D348B] text-white hover:bg-[#7678ED] rounded-full py-6 text-lg font-semibold shadow-lg transition-all hover:shadow-xl"
            >
              {loading ? 'Memproses...' : 'Daftar'}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">atau</span>
            </div>
          </div>

          <Button
            type="button"
            onClick={handleGoogleLogin}
            data-testid="register-google-btn"
            variant="outline"
            className="w-full border-2 border-gray-200 hover:bg-gray-50 hover:border-[#7678ED] rounded-full py-6 text-lg transition-all"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Daftar dengan Google
          </Button>

          <p className="text-center text-gray-600 mt-6">
            Sudah ada akaun?{' '}
            <Link to="/login" className="text-[#3D348B] font-semibold hover:text-[#7678ED] transition-colors" data-testid="register-login-link">
              Log masuk
            </Link>
          </p>
        </div>

        <p className="text-center text-white/50 mt-6 text-sm">
          <Link to="/" className="hover:text-white transition-colors">← Kembali ke laman utama</Link>
        </p>
      </div>
    </div>
  );
}
